import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { Observable, Observer, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService } from 'src/app/core/services';
import { environment } from 'src/environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonArrayService } from 'src/app/core/services/common-array.service';

@Component({
  selector: 'app-add-update-announcement',
  templateUrl: './add-update-announcement.component.html',
  styleUrls: ['./add-update-announcement.component.scss'],
})
export class AddUpdateAnnouncementComponent implements OnInit {
  addUpdateAnnouncementForm!: FormGroup;
  idForUpdate: string;
  buttonLoading = false;
  imageLoading = false;
  pageSize = 10;
  pageIndex = 1;
  checkAddPermission = false;
  checkUpdatePermission = false;
  mediaBaseUrl = environment.mediaBaseUrl;
  mediaUploadUrl = environment.apiBaseUrl + '/api/media';

  announcementData: any = null;
  moduleList = [
    { label: 'Test Series', value: 'testSery' },
    { label: 'Plan', value: 'plan' },
    { label: 'Job Alert', value: 'jobAlert' },
    { label: 'Current Affairs', value: 'currentAffair' },
    { label: 'Resource', value: 'resource' },
    { label: 'Editorial Corner', value: 'editorial' },
  ];

  moduleData: any[] = [];
  moduleLoading = false;
  moduleTotalCount = 0;
  moduleSearch = new Subject<string>();
  modulePageIndex = 1;

  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private msg: NzMessageService,
    private commonArrayService: CommonArrayService,
  ) {
    this.idForUpdate = this.activatedRoute.snapshot.params.id;
    this.initForm();
  }

  initForm(item?: any): void {
    this.addUpdateAnnouncementForm = this.fb.group({
      _id: [item?._id || null],
      title: [item?.title || null],
      redirectUrl: [item?.redirectUrl || null],
      image: [item?.image || null, [Validators.required]],
      ctaButton: [item?.ctaButton || null],
      countDown: this.fb.group({
        enabled: [item?.countDown?.enabled || false],
        endTime: [
          item?.countDown?.endTime ? new Date(item.countDown.endTime) : null,
        ],
        label: [item?.countDown?.label || 'Sale Ending In'],
      }),
      startDate: [
        item?.startDate ? new Date(item.startDate) : null,
        [Validators.required],
      ],
      endDate: [
        item?.endDate ? new Date(item.endDate) : null,
        [Validators.required],
      ],
      status: [item?.status || 'active', [Validators.required]],
      module: [item?.module || null, [Validators.required]],
      referenceId: [item?.referenceId?._id || item?.referenceId || null],
    });

    this.addUpdateAnnouncementForm
      .get('endDate')
      ?.valueChanges.subscribe((value) => {
        this.addUpdateAnnouncementForm
          .get('countDown.endTime')
          ?.patchValue(value, { emitEvent: false });
      });

    this.addUpdateAnnouncementForm
      .get('countDown.endTime')
      ?.valueChanges.subscribe((value) => {
        this.addUpdateAnnouncementForm
          .get('endDate')
          ?.patchValue(value, { emitEvent: false });
      });

    this.updateReferenceValidation(item?.module);
  }

  updateReferenceValidation(module: string): void {
    const referenceControl = this.addUpdateAnnouncementForm.get('referenceId');
    if (module === 'testSery') {
      referenceControl?.setValidators([Validators.required]);
    } else {
      referenceControl?.clearValidators();
    }
    referenceControl?.updateValueAndValidity();
  }

  ngOnInit(): void {
    if (this.idForUpdate) {
      this.getAnnouncementById();
    }
    this.initModuleSearch();
  }

  initModuleSearch(): void {
    this.moduleSearch.pipe(debounceTime(800)).subscribe((search: string) => {
      this.moduleData = [];
      this.modulePageIndex = 1;
      this.fetchModuleData(search, 0);
    });
  }

  onModuleChange(module: string): void {
    this.updateReferenceValidation(module);
    this.addUpdateAnnouncementForm.patchValue({ referenceId: null });
    this.moduleData = [];
    this.modulePageIndex = 1;
    this.moduleTotalCount = 0;
    if (module) {
      this.fetchModuleData('', 0);
    }
  }

  onModuleSearch(searchText: string): void {
    this.moduleSearch.next(searchText);
  }

  loadMoreModuleData(): void {
    if (this.moduleTotalCount > this.moduleData.length) {
      this.modulePageIndex++;
      const skip = this.pageSize * (this.modulePageIndex - 1);
      this.fetchModuleData('', skip);
    }
  }

  fetchModuleData(search: string, skip: number): void {
    const module = this.addUpdateAnnouncementForm.get('module')?.value;
    if (!module) return;

    this.moduleLoading = true;
    let url = '';
    let params: any = { skip, limit: this.pageSize };
    if (search) params.search = search;

    switch (module) {
      case 'plan':
        url = 'plans';
        params.status = true;
        params.approveStatus = 'approved';
        break;
      case 'testSery':
        url = 'test-series/for-list';
        params.approveStatus = 'approved';
        break;
      case 'jobAlert':
        url = 'job-alerts';
        params.approveStatus = 'approved';
        break;
      case 'currentAffair':
        url = 'current-affairs';
        params.status = true;
        params.approveStatus = 'approved';
        break;
      case 'resource':
        url = 'resources';
        params.status = true;
        break;
      case 'editorial':
        url = 'editorials';
        params.status = true;
        params.approveStatus = 'approved';
        break;
    }

    this.httpRequestService.request('get', url, params).subscribe(
      (result: any) => {
        this.moduleLoading = false;
        const incoming = result.data || [];
        this.moduleData =
          skip === 0 ? incoming : [...this.moduleData, ...incoming];
        this.moduleTotalCount = result.totalCount || 0;
      },
      () => {
        this.moduleLoading = false;
      },
    );
  }

  getModuleLabel(module: string): string {
    const map: any = {
      plan: 'Plan',
      testSery: 'Test Series',
      jobAlert: 'Job Alert',
      currentAffairs: 'Current Affairs',
      resource: 'Resource',
      editorials: 'Editorial Corner',
    };
    return map[module] || 'Item';
  }

  getModuleItemLabel(module: string, item: any): string {
    switch (module) {
      case 'jobAlert':
        return item.title || item.name || item._id;
      case 'currentAffairs':
      case 'editorials':
        return item.title || item._id;
      default:
        return item.name || item.title || item._id;
    }
  }

  getAnnouncementById(): void {
    this.httpRequestService
      .request('get', `announcements/${this.idForUpdate}`)
      .subscribe((result: any) => {
        const data = result.data;
        if (data) {
          this.initForm(data);
          this.fetchModuleData('', 0);
        }
      });
  }

  submit(): void {
    if (this.addUpdateAnnouncementForm.invalid) {
      this.markFormGroupTouched(this.addUpdateAnnouncementForm);
      return;
    }

    this.buttonLoading = true;
    const finalData = this.addUpdateAnnouncementForm.value;
    const method = this.idForUpdate ? 'put' : 'post';
    const url = this.idForUpdate
      ? `announcements/${this.idForUpdate}`
      : 'announcements';

    this.httpRequestService.request(method, url, finalData).subscribe(
      () => {
        this.notificationService.success(
          '',
          `Announcement ${this.idForUpdate ? 'Updated' : 'Added'} Successfully`,
        );
        this.buttonLoading = false;
        const index = this.activatedRoute.snapshot.queryParams['index'];
        this.router.navigate(['/main/announcement'], {
          queryParams: { index: index },
        });
      },
      (error: any) => {
        this.buttonLoading = false;
        this.notificationService.error(
          '',
          error.error.name || error.error.message || 'Something went wrong',
        );
      },
    );
  }

  UploadImageFile(info: { file: NzUploadFile }) {
    switch (info.file.status) {
      case 'uploading':
        this.imageLoading = true;
        break;
      case 'done':
        this.imageLoading = false;
        this.addUpdateAnnouncementForm.patchValue({
          image: info.file.response.data.path,
        });
        break;
      case 'error':
        this.msg.error('Network error');
        this.imageLoading = false;
        break;
    }
  }

  beforeRelatedMediaUpload = (
    file: NzUploadFile,
    fileList: NzUploadFile[],
  ): Observable<any> => {
    return new Observable((observer: Observer<boolean>) => {
      const isImage = file.type?.startsWith('image/');
      if (!isImage) {
        this.msg.error('You can only upload image file!');
        observer.complete();
        return;
      }
      const isLt20M = file.size ? file.size / 1024 / 1024 < 20 : false;
      if (!isLt20M) {
        this.msg.error('Image must be smaller than 20MB!');
        observer.complete();
        return;
      }
      observer.next(isImage && isLt20M);
      observer.complete();
    });
  };

  removeImage(path: string): void {
    this.addUpdateAnnouncementForm.patchValue({ image: null });
    this.httpRequestService
      .request('post', 'media/delete-media', { path })
      .subscribe(() => {
        this.notificationService.success('', 'Image Deleted Successfully');
      });
  }

  customRequestHeaders = () => {
    return { Authorization: `Bearer ${localStorage.getItem('token')}` };
  };

  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsDirty();
      control.updateValueAndValidity();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup | FormArray);
      }
    });
  }
}
